import { useState, useEffect, useRef } from "react";
import { chartData as initialChart } from "../../../mocks/marketing";

interface ChartItem {
  rank: number;
  prevRank: number;
  title: string;
  artist: string;
  streams: number;
  change: "up" | "down" | "same";
  label: string;
  isOurs?: boolean;
  justMoved?: boolean;
}

interface FloatLabel {
  id: number;
  text: string;
  rank: number;
}

export default function ChartPanel() {
  const [items, setItems] = useState<ChartItem[]>(
    initialChart.map((c) => ({ ...c, prevRank: c.prev ?? c.rank }))
  );
  const [boosting, setBoosting] = useState(false);
  const [boostsLeft, setBoostsLeft] = useState(3);
  const [floatLabels, setFloatLabels] = useState<FloatLabel[]>([]);
  const [rankUpMsg, setRankUpMsg] = useState<string | null>(null);
  const [liveStreams, setLiveStreams] = useState<Record<number, number>>({});
  const [pressBoostMsg, setPressBoostMsg] = useState<string | null>(null);
  const floatIdRef = useRef(0);

  /* ── 보도자료 배포 승인 후 자동 부스트 ── */
  useEffect(() => {
    const pending = localStorage.getItem("kap_press_boost_chart");
    if (!pending) return;
    localStorage.removeItem("kap_press_boost_chart");
    const title = localStorage.getItem("kap_press_boost_title") ?? "보도자료";
    localStorage.removeItem("kap_press_boost_title");

    // 잠깐 딜레이 후 부스트 적용 (자연스러운 연출)
    setTimeout(() => {
      const newFloats: FloatLabel[] = [];
      const boosted = initialChart.map(c => ({ ...c, prevRank: c.prev ?? c.rank })).map(item => {
        if (!item.isOurs) return item;
        const boost = Math.floor(Math.random() * 400000 + 250000);
        floatIdRef.current += 1;
        newFloats.push({ id: floatIdRef.current, text: `+${(boost / 10000).toFixed(0)}만 언론 파급`, rank: item.rank });
        return { ...item, streams: item.streams + boost };
      });
      const sorted = [...boosted].sort((a, b) => b.streams - a.streams);
      const ranked: ChartItem[] = sorted.map((item, i) => ({
        ...item,
        prevRank: item.rank,
        rank: i + 1,
        change: (i + 1) < item.rank ? "up" as const : (i + 1) > item.rank ? "down" as const : "same" as const,
        justMoved: (i + 1) < item.rank && !!item.isOurs,
      }));
      setItems(ranked);
      setFloatLabels(newFloats);
      setTimeout(() => setFloatLabels([]), 2500);
      setPressBoostMsg(`📰 "${title}" 보도자료 배포 효과 — 스트리밍 급상승!`);
      setTimeout(() => {
        setPressBoostMsg(null);
        setItems(prev => prev.map(i => ({ ...i, justMoved: false })));
      }, 5000);
    }, 800);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Live micro-tick: streams increment slightly every few seconds ── */
  useEffect(() => {
    const interval = setInterval(() => {
      setItems((prev) =>
        prev.map((item) => ({
          ...item,
          streams: item.streams + Math.floor(Math.random() * 800 + 200),
        }))
      );
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  /* ── Boost handler ── */
  const handleBoost = () => {
    if (boosting || boostsLeft <= 0) return;
    setBoosting(true);

    const boostAmounts: Record<string, number> = {};
    const newFloats: FloatLabel[] = [];

    // Calculate boosts for our songs
    const boostedItems = items.map((item) => {
      if (!item.isOurs) return item;
      const boost = Math.floor(Math.random() * 180000 + 80000);
      boostAmounts[item.title] = boost;
      floatIdRef.current += 1;
      newFloats.push({
        id: floatIdRef.current,
        text: `+${(boost / 10000).toFixed(0)}만 스트리밍`,
        rank: item.rank,
      });
      return { ...item, streams: item.streams + boost, justMoved: false };
    });

    // Re-sort by streams and re-assign ranks
    const sorted = [...boostedItems].sort((a, b) => b.streams - a.streams);
    const ranked: ChartItem[] = sorted.map((item, i) => {
      const newRank = i + 1;
      const movedUp = newRank < item.rank && item.isOurs;
      return {
        ...item,
        prevRank: item.rank,
        rank: newRank,
        change: newRank < item.rank ? "up" : newRank > item.rank ? "down" : "same",
        justMoved: movedUp,
      };
    });

    // Show float labels
    setFloatLabels(newFloats);
    setTimeout(() => setFloatLabels([]), 2200);

    // Update live stream ticker
    const liveMap: Record<number, number> = {};
    ranked.filter((i) => i.isOurs).forEach((item) => {
      liveMap[item.rank] = boostAmounts[item.title] ?? 0;
    });
    setLiveStreams(liveMap);
    setTimeout(() => setLiveStreams({}), 2500);

    // Apply ranked data
    setTimeout(() => {
      setItems(ranked);
      setBoosting(false);
      setBoostsLeft((p) => p - 1);

      // Check if any of our songs moved up
      const rankUpSong = ranked.find(
        (i) => i.isOurs && i.justMoved
      );
      if (rankUpSong) {
        setRankUpMsg(`🎉 ${rankUpSong.title.split(" (")[0]} ${rankUpSong.rank}위로 상승!`);
        setTimeout(() => setRankUpMsg(null), 3000);
      }

      // Clear justMoved
      setTimeout(() => {
        setItems((prev) => prev.map((i) => ({ ...i, justMoved: false })));
      }, 1200);
    }, 900);
  };

  const ourSongs = items.filter((i) => i.isOurs);

  return (
    <div className="flex flex-col h-full relative">

      {/* ── Press boost toast ── */}
      {pressBoostMsg && (
        <div className="absolute top-0 left-0 right-0 z-50 bg-gradient-to-r from-[#1A1A1A] to-[#2A2D3E] border border-white/10 text-white text-xs font-semibold px-4 py-2.5 rounded-xl text-center animate-slide-in-up flex items-center justify-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse shrink-0"></span>
          {pressBoostMsg}
        </div>
      )}

      {/* ── Rank-up toast ── */}
      {rankUpMsg && (
        <div className="absolute top-0 left-0 right-0 z-50 bg-gradient-to-r from-[#E8375F] to-[#FF9F43] text-white text-xs font-bold px-4 py-2 rounded-xl text-center animate-slide-in-up">
          {rankUpMsg}
        </div>
      )}

      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-[#111111] font-semibold text-sm">가상 음원차트 TOP 10</h3>
          <p className="text-[#777777] text-xs mt-0.5">실시간 순위 기준 · 3.5초마다 갱신</p>
        </div>
        <span className="text-[10px] bg-[#E8375F]/10 text-[#E8375F] px-2 py-0.5 rounded-full border border-[#E8375F]/30 animate-shimmer">
          LIVE
        </span>
      </div>

      {/* ── Chart List ── */}
      <div className="flex-1 overflow-y-auto space-y-1">
        {items.map((item) => {
          const floatForThis = floatLabels.find((f) => f.rank === item.rank);
          return (
            <div
              key={item.title}
              className={`relative flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-500 ${
                item.justMoved
                  ? "bg-[#E8375F]/15 border border-[#E8375F]/40 scale-[1.01]"
                  : item.isOurs
                  ? "bg-[#E8375F]/8 border border-[#E8375F]/20"
                  : "bg-[#252830] hover:bg-[#2A2D3E]"
              }`}
            >
              {/* Floating stream label */}
              {floatForThis && (
                <span className="absolute right-2 top-0 text-[10px] text-[#00C896] font-bold animate-float-up pointer-events-none z-10">
                  {floatForThis.text}
                </span>
              )}

              {/* Rank */}
              <div className="w-6 text-center shrink-0">
                <span
                  className={`text-sm font-bold transition-all duration-300 ${
                    item.rank <= 3
                      ? "text-[#FF9F43]"
                      : item.isOurs
                      ? "text-[#E8375F]"
                      : "text-[#8B91A8]"
                  } ${item.justMoved ? "scale-125 inline-block" : ""}`}
                >
                  {item.rank}
                </span>
              </div>

              {/* Change arrow */}
              <div className="w-4 text-center shrink-0">
                {item.change === "up" && (
                  <i className={`ri-arrow-up-line text-[#00C896] text-xs ${item.justMoved ? "animate-bounce" : ""}`}></i>
                )}
                {item.change === "down" && <i className="ri-arrow-down-line text-[#E8375F] text-xs"></i>}
                {item.change === "same" && <i className="ri-minus-line text-[#8B91A8] text-xs"></i>}
              </div>

              {/* Title */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className={`text-xs font-medium truncate ${item.isOurs ? "text-[#111111]" : "text-[#333333]"}`}>
                    {item.title}
                  </span>
                  {item.isOurs && (
                    <span className="text-[9px] bg-[#E8375F] text-white px-1.5 py-0.5 rounded-full shrink-0">
                      우리 곡
                    </span>
                  )}
                </div>
                <p className="text-[#8B91A8] text-[10px] truncate">{item.artist}</p>
              </div>

              {/* Streams */}
              <div className="text-right shrink-0">
                <div className={`text-xs font-medium tabular-nums transition-all duration-300 ${
                  item.isOurs ? "text-[#E8375F]" : "text-[#8B91A8]"
                } ${liveStreams[item.rank] ? "scale-110 inline-block" : ""}`}>
                  {(item.streams / 10000).toFixed(0)}만
                </div>
                <div className="text-[#AAAAAA] text-[9px]">{item.label}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Our songs summary ── */}
      <div className="mt-3 pt-3 border-t border-black/[0.07]">
        <div className="grid grid-cols-2 gap-2 mb-3">
          {ourSongs.map((c) => (
            <div key={c.rank} className="bg-[#E8375F]/8 border border-[#E8375F]/20 rounded-xl p-2.5 text-center">
              <div className={`text-[#E8375F] font-bold text-base tabular-nums transition-all duration-500 ${c.justMoved ? "scale-125" : ""}`}>
                {c.rank}위
              </div>
              <div className="text-[#111111] text-[11px] truncate font-medium">{c.title.split(" (")[0]}</div>
              <div className="text-[#777777] text-[10px]">{(c.streams / 10000).toFixed(0)}만 스트리밍</div>
            </div>
          ))}
        </div>

        {/* Boost button */}
        <button
          onClick={handleBoost}
          disabled={boosting || boostsLeft <= 0}
          className={`w-full py-2.5 rounded-xl text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
            boostsLeft > 0
              ? "bg-gradient-to-r from-[#E8375F] to-[#FF9F43] text-white hover:opacity-90 disabled:opacity-60"
              : "bg-black/[0.05] text-[#AAAAAA] cursor-not-allowed"
          }`}
        >
          {boosting ? (
            <span className="flex items-center justify-center gap-1.5">
              <i className="ri-loader-4-line animate-spin"></i>스트리밍 집계 중...
            </span>
          ) : boostsLeft > 0 ? (
            <span className="flex items-center justify-center gap-1.5">
              <i className="ri-bar-chart-box-line"></i>
              스트리밍 부스트 발동!
              <span className="bg-white/20 px-1.5 py-0.5 rounded-full text-[9px]">{boostsLeft}회 남음</span>
            </span>
          ) : (
            <span>오늘의 부스트 소진됨</span>
          )}
        </button>
      </div>
    </div>
  );
}
